import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield, Pill, Stethoscope, Activity, FlaskConical, AlertCircle,
  ChevronRight, Loader2, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEncounter } from '@/contexts/EncounterContext';
import { allergiesService } from '@/services/allergiesService';
import { medicationsService } from '@/services/medicationsService';
import { problemsService } from '@/services/problemsService';
import { vitalsService, VitalLoinc, VitalObservation } from '@/services/vitalsService';
import { getPatientResults, LabResult } from '@/services/labService';
import { cacheKeys } from '@/lib/cacheKeys';

interface EncounterContextRailProps {
  onJumpToAllergies: () => void;
  onJumpToMedications: () => void;
  onJumpToProblems: () => void;
  onJumpToVitals: () => void;
  onReconcileAllergies: () => void;
  onReconcileMedications: () => void;
  onCaptureVitals: () => void;
}

/**
 * Persistent right-rail showing the practitioner-relevant patient context
 * that they would otherwise have to leave the current tab to see. Read-mostly
 * — write actions defer to the existing reconciliation dialogs.
 *
 * Data is fetched from the same React Query keys VisitCockpit already uses,
 * so the rail piggybacks on cached data for free in most cases.
 */
const EncounterContextRail: React.FC<EncounterContextRailProps> = ({
  onJumpToAllergies, onJumpToMedications, onJumpToProblems, onJumpToVitals,
  onReconcileAllergies, onReconcileMedications, onCaptureVitals,
}) => {
  const { clinicId, patientId } = useEncounter();

  const { data: allergies = [], isLoading: loadingAllergies } = useQuery({
    queryKey: cacheKeys.patientAllergies(patientId, { activeOnly: true }),
    queryFn: () => allergiesService.getPatientAllergies(clinicId, patientId, { activeOnly: true }),
  });
  const { data: meds = [], isLoading: loadingMeds } = useQuery({
    queryKey: cacheKeys.patientMedications(patientId, { activeOnly: true }),
    queryFn: () => medicationsService.getPatientMedications(clinicId, patientId, { activeOnly: true }),
  });
  const { data: conditions = [], isLoading: loadingConditions } = useQuery({
    queryKey: cacheKeys.patientConditions(patientId),
    queryFn: () => problemsService.getPatientConditions(clinicId, patientId),
  });
  const { data: latestVitals, isLoading: loadingVitals } = useQuery({
    queryKey: cacheKeys.patientVitalsLatest(patientId),
    queryFn: () => vitalsService.getLatestVitals(clinicId, patientId),
  });
  const { data: labResults = [], isLoading: loadingLabs } = useQuery<LabResult[]>({
    queryKey: cacheKeys.patientLabResults(patientId),
    queryFn: () => getPatientResults(patientId),
    // Lab service errors should not blow up the rail — fall back to empty.
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const activeProblems = conditions.filter(
    c => c.category === 'problem-list-item' && c.status === 'active',
  );
  const allergiesUnconfirmed = allergies.some(a => a.verification_status === 'unconfirmed');

  const recentLabs = useMemo(() => {
    return [...(labResults || [])]
      .sort((a, b) => {
        const da = new Date(a.collection_date || a.received_at || a.created_at || 0).getTime();
        const db = new Date(b.collection_date || b.received_at || b.created_at || 0).getTime();
        return db - da;
      })
      .slice(0, 3);
  }, [labResults]);

  return (
    <div className='space-y-4'>
      {/* Allergies */}
      <Section
        icon={Shield}
        title='Allergies'
        loading={loadingAllergies}
        accent={allergiesUnconfirmed ? 'amber' : allergies.length === 0 ? 'slate' : 'emerald'}
        action={{
          label: allergiesUnconfirmed ? 'Reconcile' : 'Edit',
          onClick: allergiesUnconfirmed ? onReconcileAllergies : onJumpToAllergies,
        }}
      >
        {allergies.length === 0 ? (
          <RailEmpty>NKDA — no known drug allergies</RailEmpty>
        ) : (
          <ul className='space-y-1'>
            {allergies.slice(0, 5).map(a => (
              <li
                key={a.id}
                className='flex items-start gap-2 text-xs text-slate-700'
              >
                <span className='mt-0.5'>
                  {a.verification_status === 'unconfirmed'
                    ? <AlertCircle className='h-3 w-3 text-amber-500' />
                    : <CheckCircle2 className='h-3 w-3 text-emerald-600' />}
                </span>
                <span className='min-w-0'>
                  <span className='font-medium text-slate-900'>{a.code_display}</span>
                  {a.criticality && (
                    <span className='ml-1 text-[10px] uppercase text-slate-500'>
                      {a.criticality}
                    </span>
                  )}
                </span>
              </li>
            ))}
            {allergies.length > 5 && (
              <li className='text-[10px] text-slate-500'>+{allergies.length - 5} more</li>
            )}
          </ul>
        )}
      </Section>

      {/* Medications */}
      <Section
        icon={Pill}
        title='Active medications'
        loading={loadingMeds}
        action={meds.length > 0 ? {
          label: 'Reconcile',
          onClick: onReconcileMedications,
        } : {
          label: 'View',
          onClick: onJumpToMedications,
        }}
      >
        {meds.length === 0 ? (
          <RailEmpty>None on file</RailEmpty>
        ) : (
          <ul className='space-y-1'>
            {meds.slice(0, 5).map(m => (
              <li key={m.id} className='text-xs'>
                <span className='font-medium text-slate-900'>{m.medication_display}</span>
                {(m.dose_text || m.route) && (
                  <span className='ml-1 text-slate-500'>
                    {[m.dose_text, m.route].filter(Boolean).join(' · ')}
                  </span>
                )}
              </li>
            ))}
            {meds.length > 5 && (
              <li className='text-[10px] text-slate-500'>+{meds.length - 5} more</li>
            )}
          </ul>
        )}
      </Section>

      {/* Problem list */}
      <Section
        icon={Stethoscope}
        title='Problem list'
        loading={loadingConditions}
        action={{ label: 'View', onClick: onJumpToProblems }}
      >
        {activeProblems.length === 0 ? (
          <RailEmpty>No active problems</RailEmpty>
        ) : (
          <ul className='space-y-1'>
            {activeProblems.slice(0, 5).map(c => (
              <li key={c.id} className='text-xs'>
                <span className='font-medium text-slate-900'>{c.display}</span>
              </li>
            ))}
            {activeProblems.length > 5 && (
              <li className='text-[10px] text-slate-500'>+{activeProblems.length - 5} more</li>
            )}
          </ul>
        )}
      </Section>

      {/* Vitals */}
      <Section
        icon={Activity}
        title='Last vitals'
        loading={loadingVitals}
        action={{ label: 'Capture', onClick: onCaptureVitals }}
      >
        <LatestVitalsBlock vitals={latestVitals || {}} onSeeAll={onJumpToVitals} />
      </Section>

      {/* Recent labs */}
      <Section
        icon={FlaskConical}
        title='Recent labs'
        loading={loadingLabs}
      >
        {recentLabs.length === 0 ? (
          <RailEmpty>No lab results on file</RailEmpty>
        ) : (
          <ul className='space-y-1.5'>
            {recentLabs.map(lab => (
              <li key={lab.id} className='text-xs'>
                <div className='flex items-center justify-between gap-2'>
                  <span className='truncate font-medium text-slate-900'>
                    {lab.observations?.[0]?.loincDisplayName
                      || lab.observations?.[0]?.markerName
                      || `Lab order ${lab.order_id?.slice(-6) || ''}`}
                  </span>
                  {lab.has_critical_values && (
                    <Badge className='shrink-0 bg-rose-100 text-rose-700 hover:bg-rose-100'>
                      Critical
                    </Badge>
                  )}
                </div>
                <div className='text-[10px] text-slate-500'>
                  {lab.collection_date
                    ? new Date(lab.collection_date).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })
                    : '—'} · {lab.observations?.length || 0} marker(s)
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
};

interface SectionProps {
  icon: React.ElementType;
  title: string;
  loading?: boolean;
  accent?: 'slate' | 'amber' | 'emerald' | 'rose';
  action?: { label: string; onClick: () => void };
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  icon: Icon, title, loading, accent = 'slate', action, children,
}) => {
  const accentClass =
    accent === 'amber' ? 'border-amber-200 bg-amber-50/40'
    : accent === 'emerald' ? 'border-emerald-200/70'
    : accent === 'rose' ? 'border-rose-200 bg-rose-50/40'
    : 'border-slate-200';
  return (
    <section className={`rounded-md border ${accentClass} bg-white p-3`}>
      <div className='mb-2 flex items-center justify-between gap-2'>
        <div className='flex items-center gap-1.5'>
          <Icon className='h-3.5 w-3.5 text-slate-500' />
          <h4 className='text-xs font-semibold uppercase tracking-wide text-slate-700'>
            {title}
          </h4>
          {loading && <Loader2 className='h-3 w-3 animate-spin text-slate-400' />}
        </div>
        {action && (
          <Button
            variant='ghost'
            size='sm'
            className='h-6 px-1.5 text-[11px] text-slate-500 hover:text-slate-900'
            onClick={action.onClick}
          >
            {action.label}
            <ChevronRight className='ml-0.5 h-3 w-3' />
          </Button>
        )}
      </div>
      {children}
    </section>
  );
};

const RailEmpty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className='text-xs text-slate-500'>{children}</p>
);

const formatBp = (panel: VitalObservation | null | undefined): string | null => {
  if (!panel?.components?.length) return null;
  const sys = panel.components.find(c => c.code === VitalLoinc.SYSTOLIC_BP);
  const dia = panel.components.find(c => c.code === VitalLoinc.DIASTOLIC_BP);
  if (sys?.value != null && dia?.value != null) return `${sys.value}/${dia.value}`;
  return null;
};

const LatestVitalsBlock: React.FC<{
  vitals: Record<string, VitalObservation | null>;
  onSeeAll: () => void;
}> = ({ vitals, onSeeAll }) => {
  const bp = formatBp(vitals[VitalLoinc.BLOOD_PRESSURE_PANEL]);
  const hr = vitals[VitalLoinc.HEART_RATE]?.value_quantity_value;
  const temp = vitals[VitalLoinc.BODY_TEMPERATURE]?.value_quantity_value;
  const spo2 = vitals[VitalLoinc.OXYGEN_SATURATION]?.value_quantity_value;
  const wt = vitals[VitalLoinc.BODY_WEIGHT]?.value_quantity_value;
  const wtUnit = vitals[VitalLoinc.BODY_WEIGHT]?.value_quantity_unit;

  const items: Array<{ label: string; value: string | number; unit?: string }> = [];
  if (bp) items.push({ label: 'BP', value: bp, unit: 'mmHg' });
  if (hr != null) items.push({ label: 'HR', value: hr, unit: 'bpm' });
  if (temp != null) items.push({ label: 'Temp', value: temp, unit: '°F' });
  if (spo2 != null) items.push({ label: 'SpO₂', value: spo2, unit: '%' });
  if (wt != null) items.push({ label: 'Wt', value: wt, unit: wtUnit || 'lb' });

  if (items.length === 0) {
    return <RailEmpty>Not yet captured</RailEmpty>;
  }

  const mostRecentTs = Object.values(vitals)
    .map(v => v?.effective_date_time ? new Date(v.effective_date_time).getTime() : 0)
    .reduce((max, t) => Math.max(max, t), 0);
  const date = mostRecentTs
    ? new Date(mostRecentTs).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    : null;

  return (
    <div className='space-y-1.5'>
      <ul className='grid grid-cols-2 gap-x-3 gap-y-1 text-xs'>
        {items.map(it => (
          <li key={it.label} className='flex items-baseline gap-1'>
            <span className='text-slate-500 w-9'>{it.label}</span>
            <span className='font-medium text-slate-900'>{it.value}</span>
            {it.unit && <span className='text-[10px] text-slate-500'>{it.unit}</span>}
          </li>
        ))}
      </ul>
      {date && (
        <div className='flex items-center justify-between text-[10px] text-slate-500'>
          <span>Recorded {date}</span>
          <button
            type='button'
            onClick={onSeeAll}
            className='hover:text-slate-900'
          >
            See trend →
          </button>
        </div>
      )}
    </div>
  );
};

export default EncounterContextRail;
